import { AppLogoutConfirmComponent } from '@shared/components/app-logout-confirm/app-logout-confirm.component';
import { Component, inject, OnInit, OnDestroy, computed, effect, signal, untracked } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { AppImageViewerComponent } from '@shared/components/app-image-viewer/app-image-viewer.component';
import { AuthService } from '@shared/services/auth.service';
import { TransitionOverlayService } from '@core/services/transition-overlay.service';
import { ActiveContextService } from '@shared/services/active-context.service';
import { PermissionsService } from '@core/services/permissions.service';
import { permissionsForRoute } from '@core/config/route-permissions';
import { ZONA_POR_DEFECTO } from '@shared/utils/timezone.util';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { AppExportDockComponent } from '@shared/components/app-export-dock/app-export-dock.component';
import { SubscriptionService } from '@shared/services/subscription.service';
import { SubscriptionNotice, subscriptionNoticeFrom } from '@shared/utils/subscription-notice.util';

type MenuChild = NonNullable<MenuItem['children']>[number];

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  access?: 'all' | 'admin';
  requiresBilling?: boolean;
  requiresAccounting?: boolean;
  /** Alcance del grupo: lo que se hace en la sucursal activa o lo que es de todo el negocio. */
  scope?: 'branch' | 'business';
  /** Rótulo que se dibuja encima del primer grupo de cada alcance. */
  sectionStart?: string;
  children?: { label: string; route: string; icon: string; access: 'admin' | 'all'; requiresBilling?: boolean; requiresAccounting?: boolean; queryParams?: Record<string, string> }[];
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [AppLogoutConfirmComponent, RouterOutlet, RouterLink, RouterLinkActive, CommonModule, AppImageViewerComponent, AppButtonComponent, AppExportDockComponent],
  templateUrl: './admin-layout.html',
  styleUrl: './admin-layout.css',
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeProfileMenu()',
  },
})
export class AdminLayout implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private overlayService = inject(TransitionOverlayService);
  public activeContext = inject(ActiveContextService);
  private permissions = inject(PermissionsService);
  private subscriptionService = inject(SubscriptionService);
  private routerSub!: Subscription;

  /** Aviso de mora, suspensión o cancelación: visible en todo el panel, también al entrar a la sesión. */
  readonly subNotice = signal<SubscriptionNotice | null>(null);
  /** Se recarga al cambiar de negocio activo. */
  private readonly reloadNoticeOnBusinessChange = effect(() => {
    this.activeContext.activeBusiness();
    untracked(() => this.loadSubscriptionNotice());
  });

  // Reloj de la sucursal activa: se refresca cada 15 s (alcanza para mostrar minutos).
  private readonly clockTick = signal(Date.now());
  private clockTimer?: ReturnType<typeof setInterval>;

  /** Hora, zona IANA y desfase de la sucursal activa (la zona de la sucursal manda sobre la del negocio). */
  readonly zoneClock = computed(() => {
    const now = new Date(this.clockTick());
    const read = (tz: string) => {
      const time = new Intl.DateTimeFormat('es-EC', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
      const part = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(now).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
      const offset = part === 'GMT' ? 'UTC±00:00' : part.replace('GMT', 'UTC').replace('-', '−');
      return { tz, time, offset };
    };
    try {
      return read(this.activeContext.timezone());
    } catch {
      return read(ZONA_POR_DEFECTO); // zona inválida guardada a mano
    }
  });

  // Computed URL del logo con jerarquía: 1. Sucursal -> 2. Negocio -> 3. null
  readonly brandLogoUrl = computed(() => {
    const est = this.activeContext.activeEstablishment();
    if (est?.logoUrl) return est.logoUrl;
    const bus = this.activeContext.activeBusiness();
    if (bus?.logoUrl) return bus.logoUrl;
    return null;
  });

  /** URL del logo que ya falló al cargar: se usa el isotipo de FAISEN en su lugar (no una caja rota). */
  private readonly failedLogoUrl = signal<string | null>(null);
  readonly brandLogo = computed(() => {
    const url = this.brandLogoUrl();
    return url && url !== this.failedLogoUrl() ? url : null;
  });

  onBrandLogoError(url: string | null): void {
    if (url) this.failedLogoUrl.set(url);
  }

  // Sidebar Layout States
  isCollapsed = false;
  showMobileMenu = false;
  showProfileMenu = false;
  showLogoutConfirm = false;
  currentRoute = '';

  // Map route segments to Spanish display names
  // Expanded submenus (mapped by MenuItem.id) - Cerrados por defecto
  expandedSubmenus: { [key: string]: boolean } = {};

  // State for hover tooltips/overlay when collapsed
  activeOverlayMenu: MenuItem | null = null;
  hoveredItemForTooltip: MenuItem | null = null;
  tooltipTop = 0;
  overlayTop = 0;
  overlayTimeout: any;

  /** Definición de cada pantalla (ruta, ícono, banderas). Cómo se agrupan en el menú está en `menuItems`. */
  private readonly screens: MenuItem[] = [
    {
      id: 'inicio',
      label: 'Inicio',
      icon: 'pi pi-th-large',
      route: '/admin/inicio',
      access: 'all',
    },
    {
      id: 'inventario',
      label: 'Inventario & Productos',
      icon: 'pi pi-box',
      access: 'all',
      children: [
        {
          label: 'Catálogo de Productos',
          route: '/admin/products',
          icon: 'pi pi-tag',
          access: 'all',
        },
        {
          label: 'Control de Inventario',
          route: '/admin/inventory',
          icon: 'pi pi-list',
          access: 'all',
        },
        {
          label: 'Traspasos',
          route: '/admin/transfers',
          icon: 'pi pi-directions',
          access: 'all',
        },
        {
          label: 'Kardex',
          route: '/admin/kardex',
          icon: 'pi pi-history',
          access: 'all',
        },
      ],
    },
    {
      id: 'ventas',
      label: 'Ventas y Facturación',
      icon: 'pi pi-shopping-cart',
      access: 'all',
      children: [
        {
          label: 'Directorio de Clientes',
          route: '/admin/customers',
          icon: 'pi pi-users',
          access: 'all',
        },
        {
          label: 'Cotizaciones & Proformas',
          route: '/admin/quotations',
          icon: 'pi pi-file-edit',
          access: 'all',
        },
        {
          label: 'Gestión de Pedidos',
          route: '/admin/orders',
          icon: 'pi pi-shopping-bag',
          access: 'all',
        },
        {
          label: 'Punto de Venta (POS)',
          route: '/admin/pos',
          icon: 'pi pi-desktop',
          access: 'all',
        },
        {
          label: 'Caja y Arqueos',
          route: '/admin/cash-shifts',
          icon: 'pi pi-wallet',
          access: 'all',
        },
        {
          label: 'Ventas & Comprobantes',
          route: '/admin/invoices',
          icon: 'pi pi-receipt',
          access: 'all',
        },
        {
          label: 'Notas de Crédito',
          route: '/admin/credit-notes',
          icon: 'pi pi-replay',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Notas de Débito',
          route: '/admin/debit-notes',
          icon: 'pi pi-plus-circle',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Guías de Remisión',
          route: '/admin/remission-guides',
          icon: 'pi pi-truck',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Cuentas por Cobrar',
          route: '/admin/receivables',
          icon: 'pi pi-wallet',
          access: 'all',
        },
        {
          label: 'Retenciones Recibidas',
          route: '/admin/received-retentions',
          icon: 'pi pi-percentage',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Reportes de Ventas',
          route: '/admin/sales-reports',
          icon: 'pi pi-chart-line',
          access: 'all',
        },
        {
          label: 'Anticipos de Clientes',
          route: '/admin/customer-advances',
          icon: 'pi pi-money-bill',
          access: 'all',
        },
      ],
    },
    {
      id: 'compras',
      label: 'Compras y Gastos',
      icon: 'pi pi-truck',
      access: 'all',
      children: [
        {
          label: 'Directorio de Proveedores',
          route: '/admin/suppliers',
          icon: 'pi pi-truck',
          access: 'all',
        },
        {
          label: 'Órdenes de Compra',
          route: '/admin/purchase-orders',
          icon: 'pi pi-clipboard',
          access: 'all',
        },
        {
          label: 'Facturas de Compra',
          route: '/admin/purchase-invoices',
          icon: 'pi pi-shopping-cart',
          access: 'all',
        },
        {
          label: 'Liquidaciones de Compra',
          route: '/admin/settlements',
          icon: 'pi pi-file-edit',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Notas de Crédito',
          route: '/admin/purchase-credit-notes',
          icon: 'pi pi-replay',
          access: 'all',
        },
        {
          label: 'Notas de Débito',
          route: '/admin/purchase-debit-notes',
          icon: 'pi pi-plus-circle',
          access: 'all',
        },
        {
          label: 'Retenciones Emitidas',
          route: '/admin/retentions',
          icon: 'pi pi-percentage',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Cuentas por Pagar',
          route: '/admin/payables',
          icon: 'pi pi-wallet',
          access: 'all',
        },
        {
          label: 'Reportes de Compras',
          route: '/admin/purchase-reports',
          icon: 'pi pi-chart-bar',
          access: 'all',
        },
        {
          label: 'Anticipos a Proveedores',
          route: '/admin/supplier-advances',
          icon: 'pi pi-money-bill',
          access: 'all',
        },
      ],
    },
    {
      id: 'contabilidad',
      label: 'Contabilidad & Fiscal',
      icon: 'pi pi-book',
      access: 'all',
      requiresAccounting: true,
      children: [
        {
          label: 'Plan de Cuentas',
          route: '/admin/chart-of-accounts',
          icon: 'pi pi-sitemap',
          access: 'all',
        },
        {
          label: 'Configuración Contable',
          route: '/admin/account-mappings',
          icon: 'pi pi-link',
          access: 'all',
        },
        {
          label: 'Asiento de Apertura',
          route: '/admin/opening-balance',
          icon: 'pi pi-flag',
          access: 'all',
        },
        {
          label: 'Asientos Contables',
          route: '/admin/accounting-entries',
          icon: 'pi pi-book',
          access: 'all',
        },
        {
          label: 'Conciliación Bancaria',
          route: '/admin/bank-reconciliations',
          icon: 'pi pi-building-columns',
          access: 'all',
        },
        {
          label: 'Activos Fijos',
          route: '/admin/fixed-assets',
          icon: 'pi pi-briefcase',
          access: 'all',
        },
        {
          label: 'Periodos y Años Fiscales',
          route: '/admin/accounting-periods',
          icon: 'pi pi-calendar-times',
          access: 'all',
        },
        {
          label: 'Cierre y Ajustes',
          route: '/admin/period-end',
          icon: 'pi pi-verified',
          access: 'all',
        },
        {
          label: 'Reportes Contables',
          route: '/admin/accounting-reports',
          icon: 'pi pi-chart-bar',
          access: 'all',
        },
        {
          label: 'Reportes Financieros (NIIF)',
          route: '/admin/official-statements',
          icon: 'pi pi-book',
          access: 'all',
        },
        {
          label: 'Tesorería',
          route: '/admin/treasury',
          icon: 'pi pi-building-columns',
          access: 'all',
        },
        {
          label: 'Bancos',
          route: '/admin/bank-accounts',
          icon: 'pi pi-building-columns',
          access: 'all',
        },
        {
          label: 'Caja Chica',
          route: '/admin/petty-cash',
          icon: 'pi pi-wallet',
          access: 'all',
        },
        {
          label: 'Declaración de IVA',
          route: '/admin/iva-settlements',
          icon: 'pi pi-percentage',
          access: 'all',
        },
        {
          label: 'Buzón SRI',
          route: '/admin/sri-inbox',
          icon: 'pi pi-inbox',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'ATS (Anexo SRI)',
          route: '/admin/ats',
          icon: 'pi pi-file-export',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Conciliación con el SRI',
          route: '/admin/sri-reconciliation',
          icon: 'pi pi-check-square',
          access: 'all',
          requiresBilling: true,
        },
      ],
    },
    {
      id: 'configuraciones',
      label: 'Configuraciones',
      icon: 'pi pi-cog',
      access: 'all',
      children: [
        {
          label: 'Personal de la Sucursal',
          route: '/admin/users',
          icon: 'pi pi-users',
          access: 'all',
        },
        {
          label: 'Perfil de Sucursal',
          route: '/admin/branch-profile',
          icon: 'pi pi-building',
          access: 'all',
        },
        {
          label: 'Puntos de Emisión',
          route: '/admin/emission-points',
          icon: 'pi pi-print',
          access: 'all',
        },
        {
          label: 'Categorías & Marcas',
          route: '/admin/categories-brands',
          icon: 'pi pi-tags',
          access: 'all',
        },
        {
          label: 'Métodos de Pago',
          route: '/admin/payment-methods',
          icon: 'pi pi-wallet',
          access: 'all',
        },
        {
          label: 'Códigos de Retención',
          route: '/admin/retention-codes',
          icon: 'pi pi-percentage',
          access: 'all',
          requiresBilling: true,
        },
        {
          label: 'Config. de Tickets',
          route: '/admin/receipt-config',
          icon: 'pi pi-print',
          access: 'all',
        },
      ],
    },
    {
      id: 'empresa',
      label: 'Empresa',
      icon: 'pi pi-briefcase',
      access: 'all',
      children: [
        { label: 'Perfil del Negocio', route: '/admin/business', icon: 'pi pi-building', access: 'all' },
        { label: 'Sucursales & Establecimientos', route: '/admin/establishments', icon: 'pi pi-shop', access: 'all' },
        { label: 'Personal y Usuarios', route: '/admin/business-users', icon: 'pi pi-users', access: 'all' },
        { label: 'E-Commerce', route: '/admin/ecommerce', icon: 'pi pi-globe', access: 'all' },
        { label: 'Roles & Permisos', route: '/admin/roles', icon: 'pi pi-shield', access: 'all' },
        { label: 'Mi Suscripción & Pagos', route: '/admin/subscription', icon: 'pi pi-credit-card', access: 'all' },
        { label: 'Firma Electrónica SRI', route: '/admin/certificados', icon: 'pi pi-key', access: 'all', requiresBilling: true },
      ],
    },
  ];

  private screen(route: string): MenuChild | undefined {
    for (const g of this.screens) {
      const c = g.children?.find((x) => x.route === route);
      if (c) return c;
    }
    return undefined;
  }

  private pick(...routes: string[]): MenuChild[] {
    return routes.map((r) => this.screen(r)).filter((c): c is MenuChild => !!c);
  }

  /** Pantallas que solo existen con la contabilidad activada. */
  private pickAccounting(...routes: string[]): MenuChild[] {
    return this.pick(...routes).map((c) => ({ ...c, requiresAccounting: true }));
  }

  /**
   * Menú lateral en dos zonas: lo que se hace en la sucursal activa (cambia al cambiar de sucursal) y lo que es de todo el negocio
   * (no cambia). Cada pantalla se toma de `screens` por su ruta para no repetir sus banderas.
   */
  menuItems: MenuItem[] = [
    { id: 'inicio', label: 'Inicio', icon: 'pi pi-th-large', route: '/admin/inicio', access: 'all' },
    // ── Esta sucursal ──
    { id: 'vender', label: 'Vender', icon: 'pi pi-shopping-cart', access: 'all', scope: 'branch',
      children: this.pick('/admin/pos', '/admin/quotations', '/admin/orders', '/admin/invoices', '/admin/credit-notes', '/admin/debit-notes', '/admin/remission-guides', '/admin/receivables', '/admin/received-retentions', '/admin/customer-advances') },
    { id: 'comprar', label: 'Comprar', icon: 'pi pi-truck', access: 'all', scope: 'branch',
      children: this.pick('/admin/purchase-orders', '/admin/purchase-invoices', '/admin/settlements', '/admin/purchase-credit-notes', '/admin/purchase-debit-notes', '/admin/retentions', '/admin/payables', '/admin/supplier-advances') },
    { id: 'caja', label: 'Caja', icon: 'pi pi-wallet', access: 'all', scope: 'branch',
      children: [...this.pick('/admin/cash-shifts', '/admin/bank-accounts', '/admin/petty-cash')] },
    { id: 'inventario', label: 'Inventario', icon: 'pi pi-box', access: 'all', scope: 'branch',
      children: this.pick('/admin/inventory', '/admin/transfers', '/admin/kardex') },
    { id: 'ajustes-sucursal', label: 'Ajustes de sucursal', icon: 'pi pi-sliders-h', access: 'all', scope: 'branch',
      children: this.pick('/admin/users', '/admin/branch-profile', '/admin/emission-points', '/admin/receipt-config') },
    // ── Todo el negocio ──
    { id: 'catalogos', label: 'Catálogos', icon: 'pi pi-book', access: 'all', scope: 'business',
      children: this.pick('/admin/products', '/admin/categories-brands', '/admin/customers', '/admin/suppliers') },
    { id: 'contabilidad', label: 'Contabilidad', icon: 'pi pi-calculator', access: 'all', scope: 'business', requiresAccounting: true,
      children: [...this.pickAccounting('/admin/chart-of-accounts', '/admin/account-mappings', '/admin/opening-balance', '/admin/accounting-entries', '/admin/bank-reconciliations'), ...this.pick('/admin/fixed-assets'), ...this.pickAccounting('/admin/accounting-periods', '/admin/period-end', '/admin/treasury')] },
    { id: 'impuestos', label: 'Impuestos y SRI', icon: 'pi pi-percentage', access: 'all', scope: 'business',
      children: [...this.pickAccounting('/admin/iva-settlements'), ...this.pick('/admin/sri-inbox', '/admin/ats', '/admin/sri-reconciliation', '/admin/retention-codes', '/admin/certificados')] },
    { id: 'reportes', label: 'Reportes', icon: 'pi pi-chart-bar', access: 'all', scope: 'business',
      children: [...this.pick('/admin/sales-reports', '/admin/purchase-reports'), ...this.pickAccounting('/admin/accounting-reports', '/admin/official-statements')] },
    { id: 'empresa', label: 'Empresa', icon: 'pi pi-briefcase', access: 'all', scope: 'business',
      children: this.pick('/admin/business', '/admin/establishments', '/admin/business-users', '/admin/roles', '/admin/subscription', '/admin/payment-methods', '/admin/ecommerce') },
  ];

  filteredMenuItems = computed(() => {
    const user = this.authService.currentUser();
    const isAdmin = !!user?.isAdmin;
    const isBillingEnabled = !!this.activeContext.activeBusiness()?.billingEnabled;
    const isAccountingEnabled = (this.activeContext.activeBusiness() as any)?.accountingEnabled === true;
    this.permissions.permissions(); // dependencia reactiva: el menú se recalcula al cambiar de sucursal
    const allowed = (route?: string) => !route || this.permissions.canAny(permissionsForRoute(route));

    return this.menuItems
      .filter(item => {
        if (item.access === 'admin' && !isAdmin) return false;
        if (item.requiresBilling && !isBillingEnabled) return false;
        if (item.requiresAccounting && !isAccountingEnabled) return false;
        if (!item.children && !allowed(item.route)) return false;
        return true;
      })
      .map(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            if (child.access === 'admin' && !isAdmin) return false;
            if (child.requiresBilling && !isBillingEnabled) return false;
            if (child.requiresAccounting && !isAccountingEnabled) return false;
            if (!allowed(child.route)) return false;
            return true;
          });
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(item => {
        if (item.children && item.children.length === 0) return false;
        return true;
      })
      .map((item, i, all) => ({ ...item, sectionStart: item.scope && all.findIndex((x) => x.scope === item.scope) === i ? (item.scope === 'branch' ? 'Esta sucursal' : 'Todo el negocio') : undefined }));
  });

  get currentUser() {
    const user = this.authService.currentUser();
    if (!user) {
      return {
        name: 'Invitado',
        email: '',
        role: 'Invitado',
        initials: 'IN'
      };
    }
    const name = user.name || user.fullName || user.email || '';
    const email = user.email || '';
    const initials = name
      .split(' ')
      .filter((n: string) => n.length > 0)
      .map((n: string) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase() || 'US';
    return {
      name: name || email,
      email,
      role: (user as any).roleName || ((user as any).roleIdentifier ? (user as any).roleIdentifier : (user.isAdmin ? 'Administrador' : 'Colaborador')),
      initials
    };
  }

  ngOnInit() {
    this.clockTimer = setInterval(() => this.clockTick.set(Date.now()), 15_000);
    // Apagar el overlay de transición: ya estamos montados en el admin panel
    this.overlayService.hide();

    this.currentRoute = this.router.url;

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      const wasOnSubscription = this.onSubscriptionPage;
      this.currentRoute = event.urlAfterRedirects;
      this.showMobileMenu = false; // close mobile menu on navigation
      // Al salir de «Mi suscripción» el aviso se recalcula: un pago reportado ya cambia el estado
      if (wasOnSubscription && !this.onSubscriptionPage) this.loadSubscriptionNotice();
    });
  }

  /** En «Mi suscripción» el estado ya se muestra en la propia pantalla. */
  get onSubscriptionPage(): boolean {
    return this.currentRoute.startsWith('/admin/subscription');
  }

  goToSubscription(): void {
    this.router.navigate(['/admin/subscription']);
  }

  private loadSubscriptionNotice(): void {
    this.subscriptionService.getMyPlan().subscribe({
      next: (plan) => this.subNotice.set(subscriptionNoticeFrom(plan)),
      error: () => this.subNotice.set(null),
    });
  }

  ngOnDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    // When collapsing, close expanded submenus
    if (this.isCollapsed) {
      Object.keys(this.expandedSubmenus).forEach(key => {
        this.expandedSubmenus[key] = false;
      });
      this.activeOverlayMenu = null;
    }
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }

  closeProfileMenu() {
    this.showProfileMenu = false;
  }

  /** Clic en cualquier lugar fuera del botón y del panel del menú de usuario (incluida la barra lateral): se cierra. */
  onDocumentClick(event: Event) {
    if (!this.showProfileMenu) return;
    const target = event.target as HTMLElement | null;
    if (!target?.closest('[data-profile-menu]')) this.showProfileMenu = false;
  }



  toggleSubmenu(menuId: string, event: Event) {
    if (this.isCollapsed) {
      const item = this.menuItems.find(m => m.id === menuId);
      if (item && item.children) {
        this.activeOverlayMenu = this.activeOverlayMenu?.id === menuId ? null : item;
      }
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    this.expandedSubmenus[menuId] = !this.expandedSubmenus[menuId];
  }

  isSubmenuExpanded(menuId: string): boolean {
    if (this.isCollapsed) return false;
    return !!this.expandedSubmenus[menuId];
  }

  isMenuOrChildActive(item: MenuItem): boolean {
    if (item.route && this.currentRoute.startsWith(item.route)) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => this.currentRoute.startsWith(child.route));
    }
    return false;
  }

  autoExpandActiveSubmenu(url: string) {
    for (const item of this.menuItems) {
      if (item.children) {
        const hasActiveChild = item.children.some(child =>
          child.route && (url === child.route || url.startsWith(child.route + '/'))
        );
        if (hasActiveChild) {
          this.expandedSubmenus[item.id] = true;
        }
      }
    }
  }

  // --- Hover event handlers for Collapsed sidebar overlay submenus ---
  onMouseEnterItem(item: MenuItem, event?: MouseEvent) {
    if (!this.isCollapsed) return;
    if (this.overlayTimeout) clearTimeout(this.overlayTimeout);

    if (item.children) {
      this.activeOverlayMenu = item;
      this.hoveredItemForTooltip = null;
      if (event) {
        const target = event.currentTarget as HTMLElement;
        if (target) {
          const rect = target.getBoundingClientRect();
          this.overlayTop = rect.top - 4; // Ajuste para que la flechita apunte al centro del icono
        }
      }
    } else {
      this.activeOverlayMenu = null;
      this.hoveredItemForTooltip = item;
      if (event) {
        const target = event.currentTarget as HTMLElement;
        if (target) {
          const rect = target.getBoundingClientRect();
          this.tooltipTop = rect.top + (rect.height / 2) - 16;
        }
      }
    }
  }

  onMouseLeaveItem() {
    if (!this.isCollapsed) return;
    this.overlayTimeout = setTimeout(() => {
      this.activeOverlayMenu = null;
      this.hoveredItemForTooltip = null;
    }, 300);
  }

  onOverlayMouseEnter() {
    if (this.overlayTimeout) clearTimeout(this.overlayTimeout);
  }

  onOverlayMouseLeave() {
    this.onMouseLeaveItem();
  }

  confirmLogout() {
    this.showProfileMenu = false;
    this.showLogoutConfirm = true;
  }

  changeBranch() {
    this.showProfileMenu = false;
    this.activeContext.setActiveEstablishment(null);
    this.overlayService.showAndNavigate(
      () => this.router.navigate(['/select-business']),
      { message: 'Cargando tus sucursales...' }
    );
  }

  changeBusiness() {
    this.showProfileMenu = false;
    this.activeContext.setActiveBusiness(null);
    this.activeContext.setActiveEstablishment(null);
    this.overlayService.showAndNavigate(
      () => this.router.navigate(['/select-business']),
      { message: 'Cargando tus negocios...' }
    );
  }

  logout() {
    this.showLogoutConfirm = false;
    this.authService.logout();
    this.overlayService.showAndNavigate(
      () => this.router.navigate(['/auth/login']),
      { message: 'Cerrando sesión...' }
    );
  }
}
